import { useRef } from "react";
import type { NotifiableError } from "@bugsnag/js";
import type { Editor } from "@tiptap/react";
import { isMobile } from "react-device-detect";
import { useHistory, useParams } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  DIRECT_CHAT_ROOM,
  GROUP_CHAT_ROOM,
} from "@/react/components/constants";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import { selectSendRichTextMessage } from "@/react/hooks/chatsV2/store";
import { TiptapEditor } from "@circle-react/components/shared/uikit/TipTap";
import { usePunditUserContext } from "@circle-react/contexts";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useChatRoom, useChatStore } from "@circle-react/hooks/chatsV2";
import { useTypingIndicator } from "@circle-react/hooks/chatsV2/useTypingIndicator";
import { useCanUploadMedia } from "@circle-react/hooks/useCanUploadMedia";
import { useToast } from "../../shared/uikit/ToastV2";
import {
  isChatRoomAdminOrModerator,
  isDirectChatRoom,
} from "../ChatWindowV2/helpers";
import { TypingIndicator } from "../TypingIndicator";

export interface RichTextMessageBoxProps {
  autoFocus?: boolean;
  chatRoomData?: any;
  disabled?: boolean;
  isChatPreview?: boolean;
  isChatSpace?: boolean;
  isInCourseSpace?: boolean;
  isLiveStream?: boolean;
  isLoading?: boolean;
  messageBoxId?: string;
  onClick?: () => void;
  parentMessageId?: number;
  participants?: any[];
  portalTargetId?: string;
  spaceId?: number;
  userList?: any[];
}

interface Params {
  uuid: string;
}

export const RichTextMessageBox = ({
  onClick,
  autoFocus = false,
  disabled = false,
  spaceId,
  chatRoomData,
  isChatPreview,
  parentMessageId,
  userList = [],
  isLiveStream = false,
  isChatSpace = false,
  isInCourseSpace = false,
  portalTargetId = "chat-threads-header",
  isLoading = false,
  participants = [],
  messageBoxId = "tiptapMessageTextBox",
}: RichTextMessageBoxProps) => {
  const { uuid: chatRoomUuid } = useParams<Params>();
  const { currentCommunityMember, isViewOnlyMasquerading }: any =
    usePunditUserContext();
  const sendRichTextMessage = useChatStore(selectSendRichTextMessage);
  const history: any = useHistory();
  const toast = useToast();
  const isMediaUploadAllowed = useCanUploadMedia();

  const { findOrCreateChatRoom } = useChatRoom();
  const editorRef = useRef<Editor>(null);

  const createChatRoom = async ({ richTextBody, unfurlUrls }: any) => {
    const roomKind = userList.length > 1 ? GROUP_CHAT_ROOM : DIRECT_CHAT_ROOM;
    await findOrCreateChatRoom({
      kind: roomKind,
      community_member_ids: userList.map(({ id }) => id),
      rich_text_body: richTextBody,
      shouldNavigate: true,
      unfurl_urls: unfurlUrls,
    });
  };

  const shouldAutoFocus = isMobile ? false : autoFocus;

  const isDirectMessaging = isDirectChatRoom(chatRoomData);
  const isChatAdminOrModerator = isChatRoomAdminOrModerator(chatRoomData);
  const { current_participant: currentParticipant = {} } = chatRoomData || {};
  const { isTyping, typingUserNames } = useTypingIndicator({
    enabled: !(isChatPreview || isInCourseSpace),
    chatRoomUuid: chatRoomData?.uuid,
    currentParticipant: currentParticipant,
    editorRef,
    participants: participants,
    parentMessageId: parentMessageId,
    messageBoxId,
    currentCommunityMember,
  });

  return (
    <div
      className={classNames("bg-primary relative px-5 pt-2", {
        "rounded-b-lg": isTyping && isLiveStream,
      })}
    >
      <TiptapEditor
        autofocus={shouldAutoFocus}
        disabled={disabled || isViewOnlyMasquerading}
        isMediaUploadAllowed={isMediaUploadAllowed}
        onSubmit={({
          body,
          attachments,
          localAttachments,
          localSgidToObjectMap,
          unfurlUrls,
        }: any) => {
          if (disabled) {
            return;
          }

          onClick?.();

          if (chatRoomData) {
            const richTextBody = { body, attachments };
            void sendRichTextMessage(chatRoomData.uuid, {
              richTextBody,
              localAttachments,
              localSgidToObjectMap,
              parentMessageId,
              unfurlUrls,
            }).catch(error => {
              toast.error(t("something_went_wrong"));
              notifyBugsnag(error as NotifiableError);
            });
            if (parentMessageId) {
              return;
            }
            if (isChatPreview && (chatRoomUuid == "new" || !chatRoomUuid)) {
              history.push(`/messages/${chatRoomData.uuid}`);
            }
          } else {
            void createChatRoom({
              richTextBody: { body, attachments },
              unfurlUrls,
            });
          }
        }}
        spaceId={spaceId}
        id={messageBoxId}
        editorRef={editorRef}
        portalTargetId={portalTargetId}
        isLiveStream={isLiveStream}
        isChatSpace={isChatSpace}
        shouldShowTextModifiersMenu={!isLoading}
        chatProps={{
          disableGroupMentions: isLiveStream || isInCourseSpace,
          isThread: !!parentMessageId,
          isDirectMessaging: isDirectMessaging,
          isChatAdminOrModerator: isChatAdminOrModerator,
          chatParticipantsCount: chatRoomData?.chat_room_participants_count,
          isTyping,
        }}
      />
      {isTyping && <TypingIndicator typingUserNames={typingUserNames} />}
    </div>
  );
};
